const ruFormatter = new Intl.NumberFormat('ru-RU');
const ruCurrencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatNumber(value: number | string | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return '0';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';

  if (decimals === 0) {
    return ruFormatter.format(Math.round(num));
  }

  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

export function formatPercent(
  value: number | null | undefined,
  decimals = 0,
  isDecimal = false,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '0%';

  const percent = isDecimal ? value * 100 : value;
  return `${percent.toFixed(decimals)}%`;
}

export function formatCurrency(
  value: number | string | null | undefined,
  withSymbol = true,
): string {
  if (value === null || value === undefined) return withSymbol ? '0 ₽' : '0';

  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return withSymbol ? '0 ₽' : '0';

  if (withSymbol) {
    return ruCurrencyFormatter.format(num);
  }
  return formatNumber(num, 0);
}

export function parseNumber(value: string | null | undefined): number {
  if (!value || value.trim().length === 0) return 0;

  // Remove thousand separators and replace comma with dot for decimal
  const cleaned = value
    .replace(/\s/g, '') // Remove spaces (thousand separator in ru)
    .replace(/[^\d,.-]/g, '') // Remove non-numeric except decimal separators
    .replace(',', '.'); // Replace comma with dot

  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? 0 : num;
}

export function isValidNumberInput(value: string): boolean {
  if (value.trim().length === 0) return true;

  const s = value;
  let i = 0;
  // optional leading minus
  if (s.startsWith('-')) i = 1;

  let seenDecimal = false;
  for (; i < s.length; i++) {
    const ch = s[i];
    if (ch === ' ') continue; // allow spaces as thousand separators
    if (ch === '.' || ch === ',') {
      if (seenDecimal) return false; // only one decimal separator allowed
      seenDecimal = true;
      continue;
    }
    const code = s.charCodeAt(i);
    if (code < 48 || code > 57) return false; // not a digit
  }

  return true;
}

export function formatDate(
  value: string | Date | null | undefined,
  options: {
    includeTime?: boolean;
    dateStyle?: 'short' | 'medium' | 'long';
  } = {},
): string {
  if (!value) return '-';

  const { includeTime = false, dateStyle = 'short' } = options;
  const date = typeof value === 'string' ? new Date(value) : value;

  if (Number.isNaN(date.getTime())) return '-';

  const formatOptions: Intl.DateTimeFormatOptions = {};

  switch (dateStyle) {
    case 'short':
      formatOptions.day = '2-digit';
      formatOptions.month = '2-digit';
      formatOptions.year = 'numeric';
      break;
    case 'medium':
      formatOptions.day = 'numeric';
      formatOptions.month = 'short';
      formatOptions.year = 'numeric';
      break;
    case 'long':
      formatOptions.day = 'numeric';
      formatOptions.month = 'long';
      formatOptions.year = 'numeric';
      break;
  }

  if (includeTime) {
    formatOptions.hour = '2-digit';
    formatOptions.minute = '2-digit';
  }

  return date.toLocaleDateString('ru-RU', formatOptions);
}
